import { AlertTriangle, ArrowLeft, BadgeCheck, ExternalLink, Network, Pencil, Tag, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { ChannelModelRecord, ChannelRecord, UpdateChannelRequest } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useChannelsList, useUpdateChannel } from '../api/useChannels.ts';
import { useChannelModelsList, useDeleteChannelModel } from '../api/useChannelModels.ts';
import {
  useNamespaceRenamePreflight,
  useRefreshNamespaceSkuNames,
  useRenameNamespace,
} from '../api/useNamespaces.ts';
import { useRoutingDefaultSkuTargets } from '../api/useTenantRouting.ts';
import {
  ROLE_OPS_ADMIN,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { ChannelBrandBindingDrawer } from '../components/ChannelBrandBindingDrawer.tsx';
import { formatLocalDate } from '../components/formatters.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { brandLabel, enabledBrandProfiles } from '../lib/channelBrands.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Panel,
  Skeleton,
  Switch,
  Table,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * Namespace detail (epic #898 P6, #910): registry fields + the SKU
 * catalog for one namespace.
 *
 * SKU creation left this page in epic #1327 PR-9. It lives on the
 * marketplace now, because listing a model is a pricing decision — the
 * sales guide multiplier and the official-list-price precondition both
 * belong to the domain that sells the SKU, and neither was reachable
 * from here. Delisting stays: it acts on a row this catalog already
 * shows, and its blast-radius warnings are namespace-scoped.
 */

const REFRESH_SNAPSHOTS_SCRIPT = 'scripts/refresh-sku-name-snapshots.mjs';

// Mirrors platform-api's channel slug rule (#996).
const SLUG_RE = /^[a-z][a-z0-9-]{0,62}$/;

// Mirrors platform-api's harness rule (#1562, src/util/harnesses.ts). Client
// side it only buys a faster, more specific message — the server still
// rejects, and its INVALID_PAYLOAD is what the operator sees if these drift.
const HARNESS_RE = /^[a-z][a-z0-9-]{0,31}$/;
const MAX_HARNESSES = 16;

type HarnessAppend =
  | { readonly ok: true; readonly harnesses: readonly string[] }
  | { readonly ok: false; readonly reason: 'invalid' | 'too_many' };

/**
 * #1562: folding one typed entry into the set.
 *
 * Extracted so the Add button and the Save button apply the SAME rule. They
 * used not to: Add ran this logic and Save read only the committed chips, so a
 * value typed and not Enter-ed was dropped while the save reported success.
 */
function appendHarness(value: readonly string[], raw: string): HarnessAppend {
  const entry = raw.trim();
  // Nothing typed is not an error — it is the ordinary case on save.
  if (entry.length === 0) return { ok: true, harnesses: value };
  if (!HARNESS_RE.test(entry)) return { ok: false, reason: 'invalid' };
  if (value.includes(entry)) return { ok: true, harnesses: value };
  if (value.length >= MAX_HARNESSES) return { ok: false, reason: 'too_many' };
  return { ok: true, harnesses: [...value, entry] };
}

const RENAME_ROLES = [ROLE_OPS_ADMIN, ROLE_OPS_SUPPLY_DEMAND_MANAGER] as const;

interface SlugRename {
  readonly from: string;
  readonly to: string;
}

/**
 * #996: first-class slug rename. Shows canonical-name usage of the current
 * slug (a lower bound — see CanonicalModelUsageResponse) and requires typing
 * the new slug again when that usage is non-zero. Bare-name (mapping) traffic
 * is unaffected by design; old canonical names 404 after the rename.
 */
function RenameSlugDialog({
  channel,
  open,
  onOpenChange,
  onRenamed,
}: {
  readonly channel: ChannelRecord;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onRenamed: (refreshError: string | null) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [slug, setSlug] = useState('');
  const [confirmSlug, setConfirmSlug] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const preflightQ = useNamespaceRenamePreflight(channel.id, open);
  const renameMut = useRenameNamespace();

  const trimmed = slug.trim();
  const slugValid = SLUG_RE.test(trimmed);
  const slugChanged = trimmed !== channel.slug;
  const usage = preflightQ.data?.canonical_usage ?? null;
  // A failed preflight means the blast radius is UNKNOWN — degrade to the
  // strong-confirmation path, never to the frictionless one (#996 Q1/AC2).
  const needsTypedConfirm = preflightQ.isError || (usage !== null && usage.requestCount > 0);
  const confirmSatisfied = !needsTypedConfirm || confirmSlug.trim() === trimmed;
  const canSubmit =
    slugValid && slugChanged && confirmSatisfied && !preflightQ.isLoading && !renameMut.isPending;

  function close(): void {
    setSlug('');
    setConfirmSlug('');
    setSubmitError(null);
    onOpenChange(false);
  }

  function submit(): void {
    if (!canSubmit) return;
    setSubmitError(null);
    renameMut.mutate(
      { id: channel.id, slug: trimmed },
      {
        onSuccess: (res) => {
          toast.success(
            res.refresh.ok
              ? t('namespace_detail.toast.renamed', { from: res.old_slug, to: res.new_slug })
              : t('namespace_detail.toast.renamed_refresh_failed', { from: res.old_slug, to: res.new_slug }),
          );
          onRenamed(res.refresh.ok ? null : res.refresh.error ?? t('namespace_detail.rename_refresh_unknown_error'));
          close();
        },
        onError: (err) => {
          setSubmitError(coerceErrorMessage(err));
        },
      },
    );
  }

  const slugHint = !slugValid && trimmed.length > 0
    ? t('namespace_detail.rename_modal.slug_invalid')
    : !slugChanged && trimmed.length > 0
      ? t('namespace_detail.rename_modal.slug_unchanged')
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next) close(); }}
      title={t('namespace_detail.rename_modal.title')}
      closeLabel={t('common.close')}
      testId="namespace-rename-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={renameMut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={needsTypedConfirm ? 'danger' : 'primary'}
            onClick={submit}
            disabled={!canSubmit}
            loading={renameMut.isPending}
            data-testid="namespace-rename-submit"
          >
            {t('namespace_detail.rename_modal.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-low">{t('namespace_detail.rename_modal.hint')}</p>
        <div className="text-body text-paper-mid">
          <span className="text-label text-paper-low">{t('namespace_detail.rename_modal.current_label')}</span>
          <span className="ml-2 font-mono">{channel.slug}</span>
        </div>
        <FormField fieldId="namespace-rename-slug" label={t('namespace_detail.rename_modal.new_slug_label')} hint={slugHint ?? undefined}>
          <Input
            id="namespace-rename-slug"
            mono
            value={slug}
            invalid={slugHint !== null}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="namespace-rename-slug-input"
          />
        </FormField>

        {preflightQ.isLoading ? (
          <p className="m-0 text-dense text-paper-low">{t('namespace_detail.rename_modal.preflight_loading')}</p>
        ) : usage !== null && usage.requestCount === 0 ? (
          <p className="m-0 text-dense text-paper-low" data-testid="namespace-rename-preflight-zero">
            {t('namespace_detail.rename_modal.preflight_zero')}
          </p>
        ) : needsTypedConfirm ? (
          <div
            className="flex flex-col gap-3 rounded-lg border border-warn-line bg-warn-dim/50 px-4 py-3 text-dense text-warn"
            data-testid="namespace-rename-preflight-warn"
          >
            <span className="flex items-start gap-2">
              <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {preflightQ.isError
                  ? t('namespace_detail.rename_modal.preflight_error_confirm')
                  : t('namespace_detail.rename_modal.preflight_usage', {
                      count: String(usage?.requestCount ?? 0),
                      keys: String(usage?.distinctApiKeys ?? 0),
                    })}
              </span>
            </span>
            <FormField fieldId="namespace-rename-confirm" label={t('namespace_detail.rename_modal.type_to_confirm_label')}>
              <Input
                id="namespace-rename-confirm"
                mono
                value={confirmSlug}
                onChange={(e) => setConfirmSlug(e.target.value)}
                data-testid="namespace-rename-confirm-input"
              />
            </FormField>
          </div>
        ) : null}

        {submitError !== null ? (
          <p className="m-0 text-dense text-bad-bright" data-testid="namespace-rename-error">
            {submitError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * #1562: the namespace's harness tags, edited as chips.
 *
 * Page-local by design — `src/ui/` is the shared Dispatch Deck set and this is
 * one page's affordance, so it composes `Input` + `Chip` + `IconButton` here
 * rather than adding a component every other page would have to ignore.
 *
 * The `datalist` offers values already in use elsewhere but does NOT restrict
 * input: a new harness has to be typeable the first time somebody adds it,
 * which is exactly when no suggestion for it can exist yet.
 */
function HarnessEditor({
  value,
  draft,
  error,
  suggestions,
  onDraftChange,
  onRemove,
  onCommitDraft,
}: {
  readonly value: readonly string[];
  readonly draft: string;
  readonly error: string | null;
  readonly suggestions: readonly string[];
  readonly onDraftChange: (next: string) => void;
  readonly onRemove: (harness: string) => void;
  readonly onCommitDraft: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" data-testid="namespace-harness-editor">
      {value.length === 0 ? (
        <p className="m-0 text-dense text-paper-faint" data-testid="namespace-harness-empty">
          {t('namespace_detail.harnesses.empty')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {value.map((harness) => (
            <Chip key={harness} tone="neutral" lamp={false} className="font-mono">
              <span data-testid={`namespace-harness-chip-${harness}`}>{harness}</span>
              <IconButton
                size="sm"
                label={t('namespace_detail.harnesses.remove', { harness })}
                onClick={() => onRemove(harness)}
                data-testid={`namespace-harness-remove-${harness}`}
                className="text-paper-low hover:bg-bad-dim hover:text-bad"
              >
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </IconButton>
            </Chip>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          id="namespace-edit-harness-input"
          mono
          list="namespace-harness-suggestions"
          value={draft}
          placeholder={t('namespace_detail.harnesses.placeholder')}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter adds a tag; it must not submit the surrounding info form,
            // which would save the namespace with the chip still unadded.
            if (e.key === 'Enter') { e.preventDefault(); onCommitDraft(); }
          }}
          data-testid="namespace-harness-input"
        />
        <Button variant="secondary" size="sm" onClick={onCommitDraft} data-testid="namespace-harness-add">
          {t('namespace_detail.harnesses.add')}
        </Button>
      </div>
      <datalist id="namespace-harness-suggestions">
        {suggestions.map((suggestion) => (
          <option key={suggestion} value={suggestion} />
        ))}
      </datalist>
      {error !== null ? (
        <p className="m-0 text-dense text-bad-bright" role="alert" data-testid="namespace-harness-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function BrandProfileChips({ channel }: { readonly channel: ChannelRecord }): JSX.Element {
  const profiles = enabledBrandProfiles(channel);
  if (profiles.length === 0) return <div className="mt-3 text-dense text-paper-faint">{'—'}</div>;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {profiles.map((profile) => (
        <Chip key={profile.brand} tone="signal" lamp={false}>
          <span>{brandLabel(profile.brand)}</span>
          {profile.public_slug ? <span className="font-mono font-normal">{profile.public_slug}</span> : null}
        </Chip>
      ))}
    </div>
  );
}

function BrandBindingsSummary({
  channel,
  onManage,
}: {
  readonly channel: ChannelRecord;
  readonly onManage: () => void;
}): JSX.Element {
  const t = useT();
  const profiles = enabledBrandProfiles(channel);
  return (
    <Panel
      caption={t('channels.brand_bindings.summary_title')}
      actions={
        <Button variant="secondary" size="sm" onClick={onManage} data-testid="namespace-manage-brands-btn">
          <Network aria-hidden="true" className="h-3.5 w-3.5" />
          {t('channels.brand_bindings.manage')}
        </Button>
      }
      padded
    >
      <p className="m-0 mb-3 text-dense text-paper-low">{t('channels.brand_bindings.summary_hint')}</p>
      {profiles.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-well px-4 py-4 text-body text-paper-low">
          {t('channels.brand_bindings.empty_bound')}
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {profiles.map((profile) => (
            <div key={profile.brand} className="rounded-lg border border-line-soft bg-well px-4 py-3">
              <div className="text-body font-medium text-paper">{brandLabel(profile.brand)}</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="min-w-0">
                  <div className="text-label text-paper-low">
                    {t('channels.brand_profiles.public_slug')}
                  </div>
                  <div className="mt-1 truncate font-mono text-dense text-paper-mid">
                    {profile.public_slug ?? channel.slug}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-label text-paper-low">
                    {t('channels.brand_profiles.display_name')}
                  </div>
                  <div className="mt-1 truncate text-dense text-paper-mid">
                    {profile.display_name ?? channel.display_name}
                  </div>
                </div>
              </div>
              {(profile.description ?? channel.description) != null ? (
                <p
                  className="m-0 mt-3 text-dense text-paper-low"
                  data-testid={`namespace-brand-summary-description-${profile.brand}`}
                >
                  {profile.description ?? channel.description}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/**
 * Delist confirm: names the SKU and its dependents — enabled default-pool
 * routing targets (counted live from the #909 SKU endpoints) plus a
 * generic org-override warning (no ops endpoint exposes those counts
 * cheaply, and inventing one is out of scope here).
 */
function SkuDelistConfirm({
  sku,
  loading,
  onCancel,
  onConfirm,
}: {
  readonly sku: ChannelModelRecord | null;
  readonly loading: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): JSX.Element {
  const t = useT();
  const poolTargets = useRoutingDefaultSkuTargets(sku?.sku_id ?? null);
  const enabledCount = (poolTargets.data?.targets ?? []).filter((target) => target.enabled).length;
  return (
    <ConfirmDialog
      open={sku !== null}
      onOpenChange={(open) => {
        if (!open && !loading) onCancel();
      }}
      title={t('namespace_detail.delist_modal.title')}
      body={sku === null ? undefined : (
        <span className="flex flex-col gap-2">
          <span>{t('namespace_detail.delist_modal.body', { sku: sku.sku_name })}</span>
          <span
            className="font-medium text-warn"
            data-testid="namespace-delist-pool-count"
          >
            {poolTargets.isPending
              ? t('namespace_detail.delist_modal.pool_loading')
              : enabledCount > 0
                ? t('namespace_detail.delist_modal.pool_count', { count: String(enabledCount) })
                : t('namespace_detail.delist_modal.pool_none')}
          </span>
          <span className="text-paper-low" data-testid="namespace-delist-overrides-warning">
            {t('namespace_detail.delist_modal.overrides_warning')}
          </span>
        </span>
      )}
      confirmLabel={loading ? t('common.deleting') : t('namespace_detail.row.delist')}
      cancelLabel={t('common.cancel')}
      closeLabel={t('common.close')}
      onConfirm={onConfirm}
      tone="danger"
      loading={loading}
      testId="namespace-delist-confirm"
    />
  );
}

export function NamespaceDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const toast = useToast();
  const channelsQ = useChannelsList();
  const skusQ = useChannelModelsList(id);
  const deleteMut = useDeleteChannelModel();
  const updateChannelMut = useUpdateChannel();

  const [editOpen, setEditOpen] = useState(false);
  const [brandDrawerOpen, setBrandDrawerOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editOfficial, setEditOfficial] = useState(false);
  const [editHarnesses, setEditHarnesses] = useState<readonly string[]>([]);
  const [editHarnessDraft, setEditHarnessDraft] = useState('');
  const [editHarnessError, setEditHarnessError] = useState<string | null>(null);
  const [delistTarget, setDelistTarget] = useState<ChannelModelRecord | null>(null);
  // Set when a successful update comes back with a different slug — the
  // stored gateway/status sku_name snapshots are stale until the refresh
  // script runs, so the notice stays up for the rest of the visit.
  const [slugRename, setSlugRename] = useState<SlugRename | null>(null);
  // #996: the orchestrated rename refreshes snapshots automatically; this
  // holds the refresh error when the rename landed but the refresh failed.
  const [renameOpen, setRenameOpen] = useState(false);
  const [refreshWarn, setRefreshWarn] = useState<string | null>(null);
  const refreshMut = useRefreshNamespaceSkuNames();
  const { user } = useSession();
  const canRename = hasAnyOpsRole(user?.roles ?? [], RENAME_ROLES);

  function retryRefresh(): void {
    if (id === undefined) return;
    refreshMut.mutate(id, {
      onSuccess: (res) => {
        setRefreshWarn(null);
        toast.success(t('namespace_detail.toast.refresh_retried', { count: String(res.refreshed_rows) }));
      },
      onError: (err) => {
        toast.error(coerceErrorMessage(err));
      },
    });
  }

  const channel = useMemo(
    () => (channelsQ.data ?? []).find((c) => c.id === id) ?? null,
    [channelsQ.data, id],
  );

  const skus = useMemo(() => skusQ.data ?? [], [skusQ.data]);

  // #1562: suggestions are whatever the fleet already uses. Sourced from the
  // channel list this page already holds, so it costs no extra request.
  const harnessSuggestions = useMemo(
    () => [...new Set((channelsQ.data ?? []).flatMap((row) => row.harnesses ?? []))].sort(),
    [channelsQ.data],
  );

  function confirmDelist(): void {
    if (delistTarget === null || id === undefined) return;
    deleteMut.mutate(
      { channelId: id, model: delistTarget.model },
      {
        onSuccess: () => {
          toast.success(t('namespace_detail.toast.model_removed'));
          setDelistTarget(null);
        },
        onError: (err) => {
          // #1374: a SKU that has been through an activation plan (or has
          // org permission rows) is FK-held and cannot be deleted yet.
          toast.error(
            err instanceof OpsApiError && err.code === 'CHANNEL_MODEL_DEPENDENCIES_EXIST'
              ? t('namespace_detail.toast.model_remove_blocked')
              : coerceErrorMessage(err),
          );
          setDelistTarget(null);
        },
      },
    );
  }

  /**
   * #1562: folds whatever is sitting in the harness input into the set and
   * returns the result, or the refusal message when it cannot be folded.
   *
   * Returning the value rather than relying on state is deliberate — the save
   * handler needs the post-commit set in the SAME tick it builds the request
   * body, and `setEditHarnesses` has not landed by then. The message is handed
   * back for the same reason: the caller decides which surface it belongs on.
   */
  function commitHarnessDraft():
    | { readonly ok: true; readonly harnesses: readonly string[] }
    | { readonly ok: false; readonly message: string } {
    const appended = appendHarness(editHarnesses, editHarnessDraft);
    if (!appended.ok) {
      const message = appended.reason === 'invalid'
        ? t('namespace_detail.harnesses.invalid')
        : t('namespace_detail.harnesses.too_many', { max: String(MAX_HARNESSES) });
      setEditHarnessError(message);
      return { ok: false, message };
    }
    setEditHarnessError(null);
    setEditHarnessDraft('');
    setEditHarnesses(appended.harnesses);
    return { ok: true, harnesses: appended.harnesses };
  }

  function handleInfoSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (id === undefined || channel === null) return;

    const fd = new FormData(e.currentTarget);
    const displayName = String(fd.get('display_name') ?? '').trim();
    const description = String(fd.get('description') ?? '').trim();
    const supplierName = String(fd.get('supplier_name') ?? '').trim();

    if (displayName.length === 0) {
      setEditError(t('namespace_detail.edit_modal.display_name_required'));
      return;
    }

    // #1562: a value still in the harness input is a value the operator typed
    // and expects to save. Fold it in rather than dropping it and reporting
    // success. When it CANNOT be folded (bad grammar, or the cap is already
    // full) the save is refused instead of guessed at — silently discarding
    // the entry is the failure this exists to prevent, and silently accepting
    // an unmatched spelling is the other half of it.
    //
    // The refusal is also written to the submit-adjacent box, not only to the
    // field-level slot: re-pressing Save with a message already sitting in
    // that slot is a React no-op, so the operator would get no request, no
    // toast, no closed modal and no pixel change from a button they pressed.
    const committed = commitHarnessDraft();
    if (!committed.ok) {
      setEditError(committed.message);
      return;
    }
    const harnesses = committed.harnesses;

    const body: UpdateChannelRequest = {
      display_name: displayName,
      description: description.length > 0 ? description : null,
      supplier_name: supplierName.length > 0 ? supplierName : null,
      is_official: editOfficial,
      // #1562: whole-set replacement — an operator who removed every chip is
      // asking for none, so the empty array has to be sent, not omitted.
      harnesses,
    };

    const previousSlug = channel.slug;
    setEditError(null);
    updateChannelMut.mutate(
      { id, body },
      {
        onSuccess: (response) => {
          toast.success(t('namespace_detail.toast.info_updated'));
          setEditOpen(false);
          if (response.channel.slug !== previousSlug) {
            setSlugRename({ from: previousSlug, to: response.channel.slug });
          }
        },
        onError: (err) => {
          const message = coerceErrorMessage(err);
          setEditError(message);
          toast.error(message);
        },
      },
    );
  }

  if (channelsQ.isLoading || skusQ.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-body text-bad-bright">
        {t('namespace_detail.not_found')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <Link
              to="/supply/namespaces"
              className="text-paper-low transition-colors duration-120 hover:text-paper"
            >
              <ArrowLeft aria-hidden="true" className="h-5 w-5" />
            </Link>
            <span>{channel.display_name}</span>
            {channel.is_official ? (
              <BadgeCheck aria-hidden="true" className="h-4 w-4 text-signal" />
            ) : null}
          </span>
        }
        actions={
          <>
            {canRename ? (
              <Button
                variant="secondary"
                onClick={() => setRenameOpen(true)}
                data-testid="namespace-rename-slug-btn"
              >
                <Tag aria-hidden="true" className="h-4 w-4" />
                {t('namespace_detail.rename_slug')}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => {
                setEditError(null);
                setEditOfficial(channel.is_official);
                setEditHarnesses(channel.harnesses ?? []);
                setEditHarnessDraft('');
                setEditHarnessError(null);
                setEditOpen(true);
              }}
              data-testid="namespace-edit-info-btn"
            >
              <Pencil aria-hidden="true" className="h-4 w-4" />
              {t('namespace_detail.edit_info')}
            </Button>
          </>
        }
      />

      {refreshWarn !== null ? (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded-lg border border-warn-line bg-warn-dim/50 px-4 py-3 text-dense leading-5 text-warn"
          data-testid="namespace-refresh-warn"
        >
          <span className="flex min-w-0 items-start gap-2">
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('namespace_detail.rename_refresh_warn', { error: refreshWarn })}</span>
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={retryRefresh}
            loading={refreshMut.isPending}
            data-testid="namespace-refresh-retry-btn"
          >
            {t('namespace_detail.rename_refresh_retry')}
          </Button>
        </div>
      ) : null}

      {slugRename !== null ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-warn-line bg-warn-dim/50 px-4 py-3 text-dense leading-5 text-warn"
          data-testid="namespace-rename-notice"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex min-w-0 flex-col gap-1">
            <span>
              {t('namespace_detail.rename_notice', { from: slugRename.from, to: slugRename.to })}
            </span>
            <code className="w-fit rounded bg-well px-2 py-0.5 font-mono text-micro text-paper">
              {REFRESH_SNAPSHOTS_SCRIPT}
            </code>
          </span>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <p className="m-0 flex flex-wrap items-center gap-2 text-body text-paper-low">
          <Chip tone="neutral" lamp={false} className="font-mono">
            {channel.vendor}
          </Chip>
          <span className="font-mono">{channel.slug}</span>
          {channel.supplier_name ? <span>· {channel.supplier_name}</span> : null}
          <span>· {skus.length} {t('namespace_detail.skus_count')}</span>
        </p>
        {channel.description != null ? (
          <p className="m-0 max-w-3xl text-body text-paper-mid" data-testid="namespace-detail-description">
            {channel.description}
          </p>
        ) : null}
        <BrandProfileChips channel={channel} />
        <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="namespace-harnesses">
          <span className="text-label text-paper-low">{t('namespace_detail.harnesses.label')}</span>
          {(channel.harnesses ?? []).length === 0 ? (
            <span className="text-dense text-paper-faint" data-testid="namespace-harnesses-none">
              {'—'}
            </span>
          ) : (
            (channel.harnesses ?? []).map((harness) => (
              <Chip key={harness} tone="signal" lamp={false} className="font-mono">
                <span data-testid={`namespace-harness-${harness}`}>{harness}</span>
              </Chip>
            ))
          )}
        </div>
      </div>

      <BrandBindingsSummary channel={channel} onManage={() => setBrandDrawerOpen(true)} />

      {skus.length === 0 ? (
        <EmptyState testId="namespace-skus-empty" title={t('namespace_detail.empty')} />
      ) : (
        <Table testId="namespace-skus-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('namespace_detail.col.model')}</Th>
              <Th>{t('namespace_detail.col.sku_name')}</Th>
              <Th>{t('namespace_detail.col.listed_at')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {skus.map((cm) => (
              <Tr key={cm.sku_id} data-testid={`namespace-sku-row-${cm.sku_id}`}>
                <Td mono emphasis>{cm.model}</Td>
                <Td mono>{cm.sku_name}</Td>
                <Td>{formatLocalDate(cm.created_at)}</Td>
                <Td className="text-right">
                  <span className="inline-flex items-center gap-1">
                    <Link
                      to={`/market/${encodeURIComponent(cm.sku_id)}`}
                      aria-label={t('namespace_detail.row.open_sku')}
                      data-testid={`namespace-sku-open-${cm.sku_id}`}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                    >
                      <ExternalLink aria-hidden="true" className="h-4 w-4" />
                    </Link>
                    <IconButton
                      size="sm"
                      label={t('namespace_detail.row.delist')}
                      onClick={() => setDelistTarget(cm)}
                      disabled={deleteMut.isPending}
                      data-testid={`namespace-sku-delist-${cm.sku_id}`}
                      className="text-bad hover:bg-bad-dim hover:text-bad"
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </IconButton>
                  </span>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!open) setEditOpen(false);
        }}
        title={t('namespace_detail.edit_modal.title')}
        description={
          <span className="inline-flex items-center gap-2">
            <Chip tone="neutral" lamp={false} className="font-mono">
              {channel.vendor}
            </Chip>
            <span className="font-mono">{channel.slug}</span>
          </span>
        }
        closeLabel={t('common.close')}
        size="lg"
      >
        <form onSubmit={handleInfoSubmit} noValidate className="flex flex-col gap-4">
          <FormField label={t('channels.create_modal.display_name')} fieldId="namespace-edit-display-name">
            <Input
              id="namespace-edit-display-name"
              name="display_name"
              defaultValue={channel.display_name}
              data-testid="namespace-edit-display-name"
            />
          </FormField>

          <FormField
            label={t('channels.create_modal.description')}
            fieldId="namespace-edit-description"
            hint={t('channels.create_modal.description_hint')}
          >
            <Textarea
              id="namespace-edit-description"
              name="description"
              rows={3}
              defaultValue={channel.description ?? ''}
              className="resize-none"
              data-testid="namespace-edit-description"
              placeholder={t('channels.create_modal.description_placeholder')}
            />
          </FormField>

          <FormField label={t('channels.create_modal.supplier_name')} fieldId="namespace-edit-supplier-name">
            <Input
              id="namespace-edit-supplier-name"
              name="supplier_name"
              defaultValue={channel.supplier_name ?? ''}
              data-testid="namespace-edit-supplier-name"
            />
          </FormField>

          <FormField
            label={t('namespace_detail.harnesses.label')}
            fieldId="namespace-edit-harness-input"
            hint={t('namespace_detail.harnesses.hint', { max: String(MAX_HARNESSES) })}
          >
            <HarnessEditor
              value={editHarnesses}
              draft={editHarnessDraft}
              error={editHarnessError}
              suggestions={harnessSuggestions}
              onDraftChange={(next) => { setEditHarnessDraft(next); setEditHarnessError(null); }}
              onRemove={(harness) => setEditHarnesses(editHarnesses.filter((e) => e !== harness))}
              onCommitDraft={() => { commitHarnessDraft(); }}
            />
          </FormField>

          <div className="flex items-center justify-between rounded-lg border border-line-soft bg-well p-4">
            <div>
              <p className="m-0 text-body font-medium text-paper">{t('channels.create_modal.is_official')}</p>
              <p className="m-0 mt-0.5 text-dense text-paper-low">{t('channels.create_modal.is_official_hint')}</p>
            </div>
            <Switch
              checked={editOfficial}
              onCheckedChange={setEditOfficial}
              testId="namespace-edit-is-official"
            />
          </div>

          {editError !== null ? (
            <div
              className="rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
              data-testid="namespace-edit-error"
              role="alert"
            >
              {editError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditOpen(false)}>
              {t('channels.create_modal.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={updateChannelMut.isPending}
              data-testid="namespace-edit-submit"
            >
              {updateChannelMut.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </Dialog>
      <ChannelBrandBindingDrawer
        channel={channel}
        isOpen={brandDrawerOpen}
        onClose={() => setBrandDrawerOpen(false)}
      />
      <SkuDelistConfirm
        sku={delistTarget}
        loading={deleteMut.isPending}
        onCancel={() => setDelistTarget(null)}
        onConfirm={confirmDelist}
      />
      <RenameSlugDialog
        channel={channel}
        open={renameOpen}
        onOpenChange={setRenameOpen}
        onRenamed={(refreshError) => setRefreshWarn(refreshError)}
      />
    </div>
  );
}
